import { LOCAL_STORAGE } from './../../config/constants';
import { BudgetStatus } from '../../types';

const GO_TO_MY_BUDGET = 'Go To My Budget';
const EVERYDOLLAR_URL = 'https://everydollar.onelink.me/7YvT/94e0435d';

type TButton = {
  cta: string;
  href?: string;
  onClick?: () => void;
  appearance: 'primary' | 'subtle' | 'ghost';
};

type StatusAttributes = {
  img: string;
  heading: string;
  text: string;
  buttons: TButton[];
};

export default function budgetStatusMap(
  status: BudgetStatus,
  transactions?: number,
  currentDate = new Date()
): StatusAttributes {
  const currentMonth = currentDate.toLocaleDateString('default', {
    month: 'long',
  });

  switch (status) {
    case BudgetStatus.NeglectedBudget:
      return {
        img:
          'https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/neglected-budget.svg',
        heading: 'Your budget misses you.',
        text:
          "Don't let the budgeting blues get you down. Achieve your goals one budget at a time!",
        buttons: [
          { cta: GO_TO_MY_BUDGET, href: EVERYDOLLAR_URL, appearance: 'ghost' },
        ],
      };
    case BudgetStatus.NewMonth:
      return {
        img:
          'https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/new-month.svg',
        heading: `It's time to create your ${currentMonth} budget!`,
        text: 'Know what time it is?',
        buttons: [
          {
            cta: `Create ${currentMonth} Budget`,
            href: EVERYDOLLAR_URL,
            appearance: 'ghost',
          },
        ],
      };
    case BudgetStatus.TrackTransactions:
      return {
        img: `https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/transactions-count-${transactions}.svg`,
        heading:
          transactions === 1
            ? `You've got 1 transaction to track!`
            : `You've got ${transactions} transactions to track!`,
        text:
          transactions === 1
            ? 'Take a minute to knock it out.'
            : 'Take a minute to knock them out.',
        buttons: [
          { cta: GO_TO_MY_BUDGET, href: EVERYDOLLAR_URL, appearance: 'ghost' },
        ],
      };
    case BudgetStatus.TrackTransactions10Plus:
      return {
        img:
          'https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/track-transactions.svg',
        heading:
          transactions && transactions > 9
            ? "You've got some transactions to track!"
            : `You've got ${transactions} transactions to track!`,
        text: 'Take a minute to knock them out.',
        buttons: [
          { cta: GO_TO_MY_BUDGET, href: EVERYDOLLAR_URL, appearance: 'ghost' },
        ],
      };
    case BudgetStatus.CaughtUp:
      return {
        img:
          'https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/caught-up.svg',
        heading: 'Boom! Your budget is <br/>up-to-date.',
        text: 'How good does that feel?',
        buttons: [
          { cta: GO_TO_MY_BUDGET, href: EVERYDOLLAR_URL, appearance: 'ghost' },
        ],
      };
    case BudgetStatus.ConnectBank:
      return {
        img:
          'https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/connect-bank.svg',
        heading: 'You can connect your bank to your budget.',
        text: 'Then those transactions will stream in automatically!',
        buttons: [
          {
            cta: 'Connect Accounts',
            href: EVERYDOLLAR_URL,
            appearance: 'ghost',
          },
          {
            cta: 'Skip',
            onClick: () =>
              window.localStorage.setItem(
                LOCAL_STORAGE.SKIP_BANK_CONNECT,
                'true'
              ),
            appearance: 'subtle',
          },
        ],
      };
    case BudgetStatus.TrackTransactionsWithoutBankConnect:
      return {
        img:
          'https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/never-budgeted.svg',
        heading: "Don't forget to track your spending this month!",
        text: "It's quick and easy with EveryDollar.",
        buttons: [
          { cta: GO_TO_MY_BUDGET, href: EVERYDOLLAR_URL, appearance: 'ghost' },
        ],
      };
    case BudgetStatus.NeverBudgeted:
    default:
      return {
        img:
          'https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/never-budgeted.svg',
        heading: 'Create your budget with EveryDollar!',
        text:
          'EveryDollar helps you tell your money where to go instead of wondering where it went.',
        buttons: [
          {
            cta: GO_TO_MY_BUDGET,
            href: EVERYDOLLAR_URL,
            appearance: 'primary',
          },
        ],
      };
  }
}
